import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { peerReviewService } from '../../services/peerReviewService';

interface PeerReviewSubmitProps {
  lessonId: number;
  lessonTitle: string;
  onSubmitted?: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const PeerReviewSubmit: React.FC<PeerReviewSubmitProps> = ({
  lessonId,
  lessonTitle,
  onSubmitted,
}) => {
  const { t } = useTranslation();
  const [submission, setSubmission] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState<{
    assignmentId: number;
    submission: string;
    attachmentFilename?: string;
    editable: boolean;
    createdAt: string;
  } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing submission on mount
  useEffect(() => {
    loadExistingSubmission();
  }, [lessonId]);

  const loadExistingSubmission = async () => {
    try {
      setInitialLoading(true);
      const data = await peerReviewService.getMySubmissionForLesson(lessonId);
      if (data) {
        setExistingSubmission(data);
        setSubmission(data.submission);
      }
    } catch (err) {
      console.error('Failed to load existing submission:', err);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError(t('peerReview.errors.fileTooLarge', 'Datei zu groß (max. 10MB)'));
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmitNew = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submission.trim()) {
      setError(t('peerReview.errors.submissionRequired', 'Bitte geben Sie Ihre Abgabe ein'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await peerReviewService.submitForReviewWithFile(lessonId, submission.trim(), file || undefined);
      setSuccess(true);
      setFile(null);
      onSubmitted?.();
      // Reload to show the saved submission
      await loadExistingSubmission();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Einreichen');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!submission.trim()) {
      setError('Bitte geben Sie Ihre Abgabe ein');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await peerReviewService.updateSubmission(lessonId, submission.trim());
      setSuccess(true);
      setIsEditing(false);
      // Reload to get fresh state
      await loadExistingSubmission();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Aktualisieren');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setSubmission(existingSubmission?.submission || '');
    setIsEditing(false);
    setError(null);
  };

  if (initialLoading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  // Existing submission — show read-only or editable
  if (existingSubmission) {
    const isLocked = !existingSubmission.editable;

    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              {t('peerReview.yourSubmission', 'Ihre Einreichung')}
            </Typography>
            {isLocked ? (
              <Chip icon={<LockIcon />} label="Review erhalten — nicht mehr änderbar" size="small" color="default" />
            ) : isEditing ? (
              <Chip label="Bearbeitung" size="small" color="info" />
            ) : (
              <Chip label="Eingereicht" size="small" color="success" />
            )}
          </Box>
          {!isLocked && !isEditing && (
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
            >
              Bearbeiten
            </Button>
          )}
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
            Einreichung erfolgreich aktualisiert.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isEditing ? (
          <>
            <TextField
              fullWidth
              multiline
              rows={8}
              label={t('peerReview.submissionLabel', 'Ihre Abgabe')}
              value={submission}
              onChange={(e) => setSubmission(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={handleCancelEdit} disabled={loading}>
                Abbrechen
              </Button>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                onClick={handleUpdate}
                disabled={loading || !submission.trim()}
              >
                Speichern
              </Button>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              p: 2,
              bgcolor: isLocked ? 'grey.50' : 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              whiteSpace: 'pre-wrap',
            }}
          >
            <Typography variant="body2">{existingSubmission.submission}</Typography>
          </Box>
        )}

        {existingSubmission.attachmentFilename && (
          <Box sx={{ mt: 1 }}>
            <Chip
              icon={<AttachFileIcon />}
              label={existingSubmission.attachmentFilename}
              size="small"
              variant="outlined"
            />
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Eingereicht am {new Date(existingSubmission.createdAt).toLocaleDateString('de-DE', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
          })}
        </Typography>
      </Paper>
    );
  }

  // No existing submission — show new submission form
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('peerReview.submitTitle', 'Abgabe zur Peer-Review einreichen')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('peerReview.submitDescription', 'Reichen Sie Ihre Arbeit zur Lektion "%{lesson}" ein. Andere Teilnehmer werden Ihre Abgabe bewerten.', { lesson: lessonTitle })}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmitNew}>
        <TextField
          fullWidth
          multiline
          rows={8}
          label={t('peerReview.submissionLabel', 'Ihre Abgabe')}
          placeholder={t('peerReview.submissionPlaceholder', 'Beschreiben Sie Ihre Lösung oder fügen Sie Ihren Text hier ein...')}
          value={submission}
          onChange={(e) => setSubmission(e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        />

        <Box sx={{ mb: 2 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip"
          />
          {file ? (
            <Chip
              icon={<AttachFileIcon />}
              label={`${file.name} (${formatFileSize(file.size)})`}
              onDelete={handleRemoveFile}
              deleteIcon={<CloseIcon />}
              sx={{ mr: 1 }}
            />
          ) : (
            <Button
              variant="outlined"
              startIcon={<AttachFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              size="small"
            >
              {t('peerReview.attachFile', 'Datei anhängen')}
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('peerReview.fileHint', 'Optional: PDF, Word, Text, Bilder oder ZIP (max. 10MB)')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            disabled={loading || !submission.trim()}
          >
            {t('peerReview.submit', 'Einreichen')}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default PeerReviewSubmit;
